import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';

import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';

const UPCOMING_APPOINTMENTS_LIMIT = 100;

export const updateProviderCalendarThunk = createAsyncThunk(
  'ProviderCalendarPage/updateProviderCalendar',
  (providerCalendar, { dispatch, rejectWithValue, extra: sdk }) =>
    sdk.currentUser
      .updateProfile({ privateData: { providerCalendar } }, { expand: true })
      .then(response => {
        const currentUser = denormalisedResponseEntities(response)[0];
        if (!currentUser) {
          throw new Error('Expected current user after saving provider calendar');
        }
        dispatch(setCurrentUser(currentUser));
        return providerCalendar;
      })
      .catch(error => rejectWithValue(storableError(error)))
);

export const updateProviderCalendar = providerCalendar => dispatch =>
  dispatch(updateProviderCalendarThunk(providerCalendar)).unwrap();

export const loadAppointmentsThunk = createAsyncThunk(
  'ProviderCalendarPage/loadAppointments',
  (_, { rejectWithValue, extra: sdk }) =>
    sdk.transactions
      .query({
        only: 'sale',
        include: ['booking', 'listing', 'customer'],
        'fields.transaction': [
          'processName',
          'lastTransition',
          'lastTransitionedAt',
          'payinTotal',
          'payoutTotal',
          'lineItems',
        ],
        'fields.listing': ['title', 'price', 'publicData.listingType'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        page: 1,
        perPage: UPCOMING_APPOINTMENTS_LIMIT,
      })
      .then(response => denormalisedResponseEntities(response))
      .catch(error => rejectWithValue(storableError(error)))
);

const slice = createSlice({
  name: 'ProviderCalendarPage',
  initialState: {
    appointments: [],
    loadInProgress: false,
    loadError: null,
    updateInProgress: false,
    updateError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadAppointmentsThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
      })
      .addCase(loadAppointmentsThunk.fulfilled, (state, action) => {
        state.loadInProgress = false;
        state.appointments = action.payload;
      })
      .addCase(loadAppointmentsThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload;
      })
      .addCase(updateProviderCalendarThunk.pending, state => {
        state.updateInProgress = true;
        state.updateError = null;
      })
      .addCase(updateProviderCalendarThunk.fulfilled, state => {
        state.updateInProgress = false;
      })
      .addCase(updateProviderCalendarThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateError = action.payload;
      });
  },
});

export default slice.reducer;

export const loadData = () => dispatch =>
  Promise.all([dispatch(fetchCurrentUser()), dispatch(loadAppointmentsThunk())]);
